import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { ModalContent, ModalWarning } from '@src/shared/modal/confirm-modal/confirm-modal.type';
import { ModalService } from '@src/shared/modal/modal.service';
import { AdServerService } from '../../../../woo_services.module/AdServerService';
import { RoutingService } from '../../../../woo_services.module/RoutingService';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { Campaign, SendToAdServerJobResult } from '../../../../woo_services.module/shared-types';

@Component({
  selector: 'send-to-ad-server-button',
  template: ` <text-button icon="ios" [disabled]="disabled" (click)="onClick()"> Skicka till adservern </text-button> `,
  standalone: false,
})
export class SendToAdServerButton {
  @Input() campaign: Campaign;
  @Input() disabled = false;

  constructor(
    private adServerService: AdServerService,
    private routingService: RoutingService,
    private translationService: TranslationService,
    private modalService: ModalService,
  ) {}

  onClick = (): void => {
    if (this.disabled) {
      return;
    }

    const content = this.getDialogContent();
    this.modalService
      .openConfirm(content)
      .then(
        (response) => {
          if (response.confirm) {
            this.sendCampaignToAdServer(this.campaign);
          }
        },
        () => null,
      )
      .catch((err) => {
        this.sendToAdServerError(err);
      });
  };

  private sendCampaignToAdServer = (campaign: Campaign) => {
    const loaderModal = this.modalService.openLoader('Skickar till adservern');
    this.adServerService
      .sendCampaign(campaign.id)
      .then(
        () => {
          loaderModal.close();
          this.sendToAdServerSuccess();
        },
        (reason) => {
          loaderModal.close();
          this.sendToAdServerError(reason);
        },
      )
      .catch(() => {
        const msg = 'Ajdå, nånting gick snett. Testa att refresha browsern och försök igen.';
        this.modalService.openError(msg);
      });
  };

  private sendToAdServerSuccess = () => {
    this.modalService.openSuccess('Kampanjen har skickats till adservern.');
    this.routingService.navigateWithReload(['/dashboard', this.campaign.id]);
  };

  private sendToAdServerError = (errorResponse: SendToAdServerJobResult) => {
    if (!!errorResponse.data_errors) {
      this.modalService.openDetailedError(
        this.translationService.convertDetailedError(errorResponse.error),
        errorResponse.data_errors,
      );
      return;
    }

    if (!(errorResponse instanceof HttpErrorResponse)) {
      const msg = this.translationService.convertSendToAdserverError(errorResponse.error);
      this.modalService.openError(msg);
    }
  };

  private getDialogContent(): ModalContent {
    const textBlocks: string[] = [];
    const warningBlocks: ModalWarning[] = [];
    const goalAdjusted =
      this.campaign.mms_gain_impressions > 0 ||
      this.campaign.additional_goal_impressions > 0 ||
      this.campaign.adjusted_goal_impressions > 0;

    if (!this.campaign.external_id) {
      textBlocks.push('Obs! Nu skickas denna kampanj till adservern. Är du säker på att du vill fortsätta?');
    }

    if (this.campaign.external_id && goalAdjusted) {
      warningBlocks.push(this.getOverrideWarningBlock());
    }
    if (this.campaign.external_id) {
      warningBlocks.push(this.getRiskOfOverrideWarningBlock());
    }

    return {
      title: 'Skicka kampanj till adservern',
      texts: textBlocks,
      confirmLabel: 'Skicka till adservern',
      cancelLabel: 'Avbryt',
      warnings: warningBlocks,
    };
  }

  private getRiskOfOverrideWarningBlock(): ModalWarning {
    return {
      title: 'Varning!',
      texts: [
        'Denna kampanj finns redan på adservern.',
        'De manuella ändringar du har gjort på adservern kommer att skrivas över och behöva göras om.',
      ],
    };
  }

  private getOverrideWarningBlock(): ModalWarning {
    return {
      title: 'Varning!',
      texts: [
        'Denna kampanj innehåller mål där visningar har justerats manuellt.',
        'Om du skickar om kampanjen till adservern så kommer detta att behöva göras om.',
      ],
    };
  }
}
